import styles from "./styles.module.scss";
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@altimateai/lego";
import { Spinner } from "@altimateai/lego";
import { RelativeComponentLoader } from "../../components/loader";
import { DataTable } from "../../components/tables";
import { Paginate } from "../../components/paginate";
import { ColorInsightTags, FilterTagWrapper } from "../../components/tags";
import TimeInfoDisclaimer from "../../components/timeInfoDisclaimer";
import classNames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { getDatasetList } from "../../helpers/apis";
import { getIconByDatastoreType } from "./DatastoreImage";
import { CopyIconButton } from "../../components/copyButton";
import { NoDatasets } from "./NoDatasets";
import { useAppState } from "@/modules/app/useAppContext";
import { Chart, ChartTypes, formatNumber } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import { APIFilterDropdown } from "@/components/filterDropdown";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterApplicableFor } from "@/components/tags/types";
import SavedFilters from "@/components/tags/SavedFilters";
import { formatCost, getInstanceEdition } from "@/helpers/utils";
import usePaginate from "@/components/paginate/usePaginate";
import { DownloadTable } from "@/components/downloadTable";
import {
  downloadAllTableDataCSV,
  getTableTagFilters,
  getTableOwnerFilters,
  getTableNameFilters,
  getSchemaFilters,
  getDatabaseFilters,
  getAnnualizedCost,
  getAnnualizedCostBreakdown,
} from "@/helpers/apis";
import { getInstanceName } from "@/helpers/utils";
import { SummaryCards } from "@/components/listSummary";
import { useBreadcrumb } from "@/components/breadcrumbs";
import {
  Alert,
  AlertDescription,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tooltip,
  Typography,
} from "@altimateai/lego";
import dayjs from "dayjs";
import { Bar } from "recharts";
import { TIME_PERIODS } from "@/helpers/constants";
import { IconInfoCircle } from "@tabler/icons-react";
import { DatastoreInstanceEditionEnum } from "../datastores/types";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import { AnnualizedCost, CostBreakdown, Dataset, TableDataFilters } from "./types";
import { Datastore } from "../datastores/types";
import { TableHeaderType, parseSortDirection } from "@/components/tables/types";

const DYNAMIC_TAG = "dynamic";

interface TableDataHeaderProps {
  numTables: number;
  tableFilters: TableDataFilters;
  tableNameFilterDispTag: Record<string, string>;
  schemaFilterDispTag: Record<string, string>;
  dbFilterDispTag: Record<string, string>;
  getFilterParams: () => TableDataFilters;
  isFiltersLoading: boolean;
  datastores: Datastore[];
}

const TableDataHeader = ({
  numTables,
  tableFilters,
  tableNameFilterDispTag,
  schemaFilterDispTag,
  dbFilterDispTag,
  getFilterParams,
  isFiltersLoading,
  datastores,
}: TableDataHeaderProps) => {
  const handleDownloadClick = async (): Promise<void> => {
    const params = getFilterParams();
    await downloadAllTableDataCSV(params);
  };
  const { updateSearchParamsByKey } = useFiltersBySearchParams();
  const { currencySymbol } = useAppState();
  return (
    <div>
      <div className="al-flex gap-md al-items-center">
        <Typography variant="page-heading">Tables</Typography>
        <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.DATASETS} />
        <div className="spacer" />
        <TimeInfoDisclaimer
          numOfSecondsActive={3}
          textToDisplay={
            <div className={classNames(styles.info_text, "text-muted m-2")}>
              Tables & costs for the past 30 days
            </div>
          }
        />
        <Card className={classNames("mb-0 no-break", styles.query_count)}>
          <CardContent className="al-pt-2 al-pb-2 al-pl-4 al-pr-4">
            <div className="al-flex al-items-center al-justify-between">
              <div>Tables: {numTables}</div>
            </div>
          </CardContent>
        </Card>
        <SavedFilters applicableFor={FilterApplicableFor.DATASET_LIST} />
        <DownloadTable handleDownload={handleDownloadClick} />
        <div id="columnpicker-container" />
      </div>
      {isFiltersLoading ? (
        <Stack className="al-items-center">
          <Spinner size="sm" />
          <span>Loading filters...</span>
        </Stack>
      ) : (
        <FilterTagWrapper
          applicableFor={FilterApplicableFor.DATASET_LIST}
          filters={{
            tableRKs: {
              searchKey: "table",
              label: "Table Name",
              filterStr: Object.keys(tableNameFilterDispTag),
              onclose: removedFilter => {
                if (!removedFilter) {
                  return;
                }
                updateSearchParamsByKey({
                  table: (tableFilters.table_rk || []).filter(
                    (filter: string) => filter !== tableNameFilterDispTag[removedFilter]
                  ),
                });
              },
            },
            schemas: {
              searchKey: "schema",
              label: "Schema",
              filterStr: Object.keys(schemaFilterDispTag),
              onclose: removedFilter => {
                if (!removedFilter) {
                  return;
                }
                updateSearchParamsByKey({
                  schema: (tableFilters.filter_schema || []).filter(
                    (filter: string) => filter !== schemaFilterDispTag[removedFilter]
                  ),
                });
              },
            },
            databases: {
              searchKey: "database",
              label: "Database",
              filterStr: Object.keys(dbFilterDispTag),
              onclose: removedFilter => {
                if (!removedFilter) {
                  return;
                }
                updateSearchParamsByKey({
                  database: (tableFilters.filter_db || []).filter(
                    (filter: string) => filter !== dbFilterDispTag[removedFilter]
                  ),
                });
              },
            },
            tagRKs: {
              label: "Tags",
              searchKey: "insight",
              filterStr: tableFilters.tag_rks || "",
            },
            totalCost: {
              searchKey: "totalcost",
              label: `${currencySymbol} Total`,
              filterStr: tableFilters.total_cost || "",
            },
            insertCost: {
              searchKey: "insertioncost",
              label: `${currencySymbol} Insertion`,
              filterStr: tableFilters.insertion_cost || "",
            },
            storageCost: {
              searchKey: "storagecost",
              label: `${currencySymbol} Storage`,
              filterStr: tableFilters.storage_cost || "",
            },
            clusteringCost: {
              searchKey: "clusteringcost",
              label: `${currencySymbol} Clustering`,
              filterStr: tableFilters.clustering_cost || "",
            },
            reads: {
              searchKey: "reads",
              label: "Reads",
              filterStr: tableFilters.min_reads || "",
            },
            writes: {
              searchKey: "writes",
              label: "Writes",
              filterStr: tableFilters.min_writes || "",
            },
            readWriteRatio: {
              searchKey: "readWriteRatio",
              label: "Read Write Ratio",
              filterStr: tableFilters.min_read_write_ratio || "",
            },
            owner: {
              searchKey: "owner",
              label: "Owner",
              filterStr: tableFilters.owners || "",
            },
            instance: {
              searchKey: "instance",
              label: "Instance",
              filterStr: tableFilters.instance_ids
                ? (tableFilters.instance_ids
                    .map((id: string) => getInstanceName(id, datastores))
                    .filter(Boolean) as string[])
                : [],
              onclose: value => {
                if (!value) {
                  return;
                }
                const instance = datastores.find((d: Datastore) => d.name === value);
                updateSearchParamsByKey({
                  instance: (tableFilters.instance_ids || []).filter(
                    (id: string) => id !== instance?.instance_id
                  ),
                });
              },
            },
          }}
        />
      )}
    </div>
  );
};

const GET_DATASET_LIST = "GET-DATASET-LIST";
const GET_ANNUALIZED_COST = "GET-ANNUALIZED-COST";
const GET_ANNUALIZED_COST_BREAKDOWN = "GET-ANNUALIZED-COST-BREAKDOWN";

interface TablePathProps {
  text: string;
  maxLength: number;
}

const TablePath = ({ text, maxLength }: TablePathProps) => {
  const truncatedText = text.length > maxLength ? text.substring(0, maxLength) + "..." : text;

  return <div className="text-muted al-break-all me-2">{truncatedText}</div>;
};

interface DatasetSummaryCardProps {
  costs: AnnualizedCost | undefined;
  isLoading: boolean;
  instanceIds: string[];
  schemaNames: string[];
  databaseNames: string[];
  tableNames: string[];
  totalCost: string | null;
  insertionCost: string | null;
  storageCost: string | null;
  clusteringCost: string | null;
  tags: string[];
  minReads: string | null;
  minWrites: string | null;
  minReadWriteRatio: string | null;
  owner: string[];
}

interface SummaryCard {
  key: string;
  label: string;
  value: string;
}

type ChartDataParams = Record<string, string | number | string[]>;

const DatasetSummaryCard = ({
  costs,
  isLoading,
  instanceIds,
  schemaNames,
  databaseNames,
  tableNames,
  totalCost,
  insertionCost,
  storageCost,
  clusteringCost,
  tags,
  minReads,
  minWrites,
  minReadWriteRatio,
  owner,
}: DatasetSummaryCardProps) => {
  const { currency } = useAppState();
  const [summaryCards, setSummaryCards] = useState<SummaryCard[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [sheetHeader, setSheetHeader] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [chartData, setChartData] = useState<CostBreakdown["breakdown"]>([]);
  const [aggregation, setAggregation] = useState<string>("day");
  const [range, setRange] = useState<{ start: string; end: string }>({
    start: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    end: dayjs().endOf("day").format("YYYY-MM-DD"),
  });

  useEffect(() => {
    setSummaryCards([
      {
        key: "annualized_total_cost",
        label: "Annualized Total Cost",
        value: formatCost(costs?.annualized_total_cost ?? 0, currency),
      },
      {
        key: "annualized_insertion_cost",
        label: "Annualized Insertion",
        value: formatCost(costs?.annualized_insertion_cost ?? 0, currency),
      },
      {
        key: "annualized_storage_cost",
        label: "Annualized Storage Cost",
        value: formatCost(costs?.annualized_storage_cost ?? 0, currency),
      },
      {
        key: "annualized_clustering_cost",
        label: "Annualized Clustering Cost",
        value: formatCost(costs?.annualized_clustering_cost ?? 0, currency),
      },
    ]);
  }, [costs, currency]);

  const { data: chartDataResult, isFetching: isChartLoading } = useQuery({
    queryKey: [
      GET_ANNUALIZED_COST_BREAKDOWN,
      instanceIds,
      schemaNames,
      databaseNames,
      tableNames,
      totalCost,
      insertionCost,
      storageCost,
      clusteringCost,
      tags,
      minReads,
      minWrites,
      minReadWriteRatio,
      owner,
      range,
      aggregation,
      selectedKey,
    ],
    queryFn: () => getAnnualizedCostBreakdown(getChartDataParams()),
    enabled: !!selectedKey,
  });

  // Handle successful chart data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (chartDataResult) {
      setChartData(chartDataResult?.breakdown ?? []);
    }
  }, [chartDataResult]);

  const getChartDataParams = (): ChartDataParams => {
    const params: ChartDataParams = {
      aggregation,
      start_date: range.start,
      end_date: range.end,
    };
    if (instanceIds && instanceIds.length > 0) {
      params["instance_ids"] = instanceIds.join();
    }
    if (schemaNames && schemaNames.length > 0) {
      params["schema_names"] = schemaNames.join();
    }
    if (databaseNames && databaseNames.length > 0) {
      params["database_names"] = databaseNames.join();
    }
    if (tableNames && tableNames.length > 0) {
      params["table_names"] = tableNames.join();
    }
    if (totalCost) {
      params["total_cost"] = totalCost;
    }
    if (insertionCost) {
      params["insertion_cost"] = insertionCost;
    }
    if (storageCost) {
      params["storage_cost"] = storageCost;
    }
    if (clusteringCost) {
      params["clustering_cost"] = clusteringCost;
    }
    if (tags && tags.length > 0) {
      params["tag_rks"] = tags;
    }
    if (minReads) {
      params["min_reads"] = minReads;
    }
    if (minWrites) {
      params["min_writes"] = minWrites;
    }
    if (minReadWriteRatio) {
      params["min_read_write_ratio"] = minReadWriteRatio;
    }
    if (owner) {
      params["owners"] = owner;
    }
    return params;
  };

  const handleOnClick = (key: string): void => {
    setSelectedKey(key);
    if (key === "annualized_total_cost") {
      setSheetHeader("Total Cost Breakdown");
      setDescription("Cost breakdown across all services and operations");
    } else if (key === "annualized_insertion_cost") {
      setSheetHeader("Insertion Cost Breakdown");
      setDescription("Cost breakdown for data ingestion and loading operations into tables");
    } else if (key === "annualized_storage_cost") {
      setSheetHeader("Storage Cost Breakdown");
      setDescription("Cost breakdown for data storage including active and time travel storage");
    } else if (key === "annualized_clustering_cost") {
      setSheetHeader("Clustering Cost Breakdown");
      setDescription(
        "Cost breakdown for automatic clustering operations to optimize query performance"
      );
    } else {
      setSheetHeader("Breakdown");
    }
    setOpen(true);
  };

  const handleSheetClose = (): void => {
    setOpen(false);
    setSelectedKey("");
    setAggregation("day");
    setRange({
      start: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
      end: dayjs().endOf("day").format("YYYY-MM-DD"),
    });
  };

  const handleDateRangeChange = (value: string): void => {
    if (value === "1") {
      setRange({
        start: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
        end: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
      });
      return;
    }
    setRange({
      start: dayjs().subtract(parseInt(value), "day").format("YYYY-MM-DD"),
      end: dayjs().endOf("day").format("YYYY-MM-DD"),
    });
  };

  return costs && summaryCards.length > 0 ? (
    <div className={"al-my-2"}>
      <SummaryCards
        isLoading={isLoading}
        selectedKey={selectedKey}
        summary={summaryCards}
        onClick={handleOnClick}
      />
      <Sheet open={open} onOpenChange={handleSheetClose}>
        <SheetContent className="!al-max-w-[50vw]" aria-label="Dataset costs modal">
          <SheetHeader className={"al-my-4"}>
            <Stack justify={"between"} align={"center"}>
              <Stack direction={"column"} gap={"none"}>
                <SheetTitle>{sheetHeader}</SheetTitle>
                <SheetDescription>{description}</SheetDescription>
              </Stack>
              <Select defaultValue={"30"} onValueChange={value => handleDateRangeChange(value)}>
                <SelectTrigger className="al-w-[150px] al-bg-white" aria-label="Select time period">
                  <SelectValue placeholder="Select a time period" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PERIODS.map(period => {
                    return (
                      <SelectItem key={period.period} value={period.period.toString(10)}>
                        <Stack align={"center"} gap={"md"}>
                          <span>{period.label}</span>
                        </Stack>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </Stack>
          </SheetHeader>
          {isChartLoading ? (
            <RelativeComponentLoader label={"Loading chart..."} />
          ) : (
            <Stack direction={"column"} className={"al-p-3 al-pb-8 al-border al-rounded-md"}>
              <Stack direction={"row"} justify={"between"} align={"center"} className={"al-mb-4"}>
                <Typography variant={"body"} weight={"regular"}>
                  Costs
                </Typography>
                <Stack align={"center"} gap={"md"}>
                  <Typography variant={"body"} weight={"regular"}>
                    Aggregation:
                  </Typography>
                  <Select defaultValue={aggregation} onValueChange={setAggregation}>
                    <SelectTrigger
                      className="al-w-[150px] al-bg-white"
                      aria-label="Select aggregation"
                    >
                      <SelectValue placeholder="Select a time period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={"day"}>Day</SelectItem>
                      <SelectItem value={"month"}>Month</SelectItem>
                    </SelectContent>
                  </Select>
                </Stack>
              </Stack>
              {chartData && (
                <Chart
                  title="Costs"
                  xAxisDataKey="date"
                  xAxisLabelFormatter={value =>
                    dayjs(value).format(aggregation === "day" ? "DD MMM YYYY" : "MMM YYYY")
                  }
                  yAxisLabelFormatter={value => formatCost(value, currency)}
                  type={ChartTypes.BarChart}
                  data={chartData}
                  tooltipProps={{
                    content: ({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) {
                        return null;
                      }
                      return (
                        <div className="al-bg-white al-border al-p-2 al-rounded-md">
                          <Stack direction={"column"} gap={"sm"} className={"al-font-semibold"}>
                            <span>
                              {dayjs(label).format(
                                aggregation === "day" ? "DD MMM YYYY" : "MMM YYYY"
                              )}
                            </span>
                            <span>Cost: {formatCost(payload[0].value, currency)}</span>
                          </Stack>
                        </div>
                      );
                    },
                  }}
                >
                  {() => {
                    const numberOfPoints = chartData.length;
                    const barSize = Math.max(20, Math.min(120, 600 / numberOfPoints)); // Dynamically calculate bar size

                    return (
                      <Bar
                        barSize={barSize} // Use dynamically calculated bar size
                        dataKey={selectedKey.slice(11)}
                        fill={"#5B9FFF"}
                        radius={[5, 5, 0, 0]}
                      />
                    );
                  }}
                </Chart>
              )}
            </Stack>
          )}
        </SheetContent>
      </Sheet>
    </div>
  ) : (
    <></>
  );
};

const DatasetsList = () => {
  useBreadcrumb("Tables");

  const { updateSearchParamsByKey, searchParams } = useFiltersBySearchParams();
  const { currency, currencySymbol, datastores } = useAppState();

  const sortAttribute = searchParams.get("sortAttribute") || "total_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"));
  const { page, pageSize: size, setPage, updatePageSize, resetPage } = usePaginate();

  const tagFilter = searchParams.getAll("insight") || [];
  const tableNameFilter = searchParams.getAll("table") || [];
  const schemaFilter = searchParams.getAll("schema") || [];
  const dbFilter = searchParams.getAll("database") || [];
  const totalCostFilter = searchParams.get("totalcost") || null;
  const insertionCostFilter = searchParams.get("insertioncost") || null;
  const storageCostFilter = searchParams.get("storagecost") || null;
  const clusteringCostFilter = searchParams.get("clusteringcost") || null;
  const readsFilter = searchParams.get("reads") || null;
  const writesFilter = searchParams.get("writes") || null;
  const readWriteRatioFilter = searchParams.get("readWriteRatio") || null;
  const ownerFilter = searchParams.getAll("owner") || [];
  const instanceFilter = searchParams.getAll("instance") || [];

  // Fetch tag filters
  const { data: tagFilters, isLoading: isTagFiltersLoading } = useQuery({
    queryKey: ["GET-TABLE-TAG-FILTERS"],
    queryFn: () => getTableTagFilters(),
  });

  const getRequestBody = (): TableDataFilters => {
    const data: TableDataFilters = { page: page + 1, size: size };
    if (tableNameFilter.length) data.table_rk = tableNameFilter;
    if (schemaFilter.length) data.filter_schema = schemaFilter;
    if (dbFilter.length) data.filter_db = dbFilter;
    if (sortAttribute) {
      data.order_by = sortAttribute;
      data.sort_order = sortOrder;
    }
    if (totalCostFilter) data.total_cost = totalCostFilter;
    if (insertionCostFilter) data.insertion_cost = insertionCostFilter;
    if (storageCostFilter) data.storage_cost = storageCostFilter;
    if (clusteringCostFilter) data.clustering_cost = clusteringCostFilter;
    if (tagFilter.length > 0) data.tag_rks = tagFilter;
    if (readsFilter) data.min_reads = readsFilter;
    if (writesFilter) data.min_writes = writesFilter;
    if (readWriteRatioFilter) data.min_read_write_ratio = readWriteRatioFilter;
    if (ownerFilter.length > 0) data.owners = ownerFilter;
    if (instanceFilter.length > 0) data.instance_ids = instanceFilter;
    return data;
  };

  const getAnnualizedCostParams = (): Record<string, string | string[]> => {
    const data: Record<string, string | string[]> = {};
    if (schemaFilter.length) {
      data["schema_names"] = schemaFilter.join();
    }
    if (dbFilter.length) {
      data["database_names"] = dbFilter.join();
    }
    if (tableNameFilter.length) {
      data["table_names"] = tableNameFilter.join();
    }
    if (instanceFilter.length > 0) data.instance_ids = instanceFilter.join();
    if (totalCostFilter) data.total_cost = totalCostFilter;
    if (insertionCostFilter) data.insertion_cost = insertionCostFilter;
    if (storageCostFilter) data.storage_cost = storageCostFilter;
    if (clusteringCostFilter) data.clustering_cost = clusteringCostFilter;
    if (readsFilter) data.min_reads = readsFilter;
    if (writesFilter) data.min_writes = writesFilter;
    if (readWriteRatioFilter) data.min_read_write_ratio = readWriteRatioFilter;
    if (tagFilter.length > 0) data.tag_rks = tagFilter;
    if (ownerFilter.length > 0) data.owners = ownerFilter;
    return data;
  };

  const {
    data: tables,
    isFetching: isTablesLoading,
    error,
  } = useQuery({
    queryKey: [
      GET_DATASET_LIST,
      page,
      size,
      ...tableNameFilter,
      ...schemaFilter,
      ...dbFilter,
      ...tagFilter,
      ...ownerFilter,
      ...instanceFilter,
      totalCostFilter,
      insertionCostFilter,
      clusteringCostFilter,
      storageCostFilter,
      readsFilter,
      writesFilter,
      readWriteRatioFilter,
      sortAttribute,
      sortOrder,
    ],
    queryFn: () => getDatasetList(getRequestBody()),
  });

  const { data: annualizedCost, isFetching: isAnnualizedCostLoading } = useQuery({
    queryKey: [
      GET_ANNUALIZED_COST,
      ...schemaFilter,
      ...dbFilter,
      ...instanceFilter,
      ...tableNameFilter,
      ...ownerFilter,
      totalCostFilter,
      insertionCostFilter,
      clusteringCostFilter,
      storageCostFilter,
      readsFilter,
      writesFilter,
      readWriteRatioFilter,
    ],
    queryFn: () => getAnnualizedCost(getAnnualizedCostParams()),
  });

  const showSnowflakeNonEnterpriseInfo = useMemo(() => {
    // return true if any of the tables have a standard edition
    if (!tables?.items?.length || !datastores) return false;
    return tables.items.some(
      table =>
        getInstanceEdition(table.instance_id, datastores) === DatastoreInstanceEditionEnum.STANDARD
    );
  }, [tables, datastores]);

  const tableNameFilterDispTag = useMemo(() => {
    const dispTags: Record<string, string> = {};
    tableNameFilter.forEach((rk: string) => {
      const tableName = rk.split("/").pop();
      if (tableName) {
        dispTags[tableName] = rk;
      }
    });
    return dispTags;
  }, [tableNameFilter]);

  const schemaFilterDispTag = useMemo(() => {
    const dispTags: Record<string, string> = {};
    schemaFilter.forEach((schema: string) => {
      dispTags[schema] = schema;
    });
    return dispTags;
  }, [schemaFilter]);

  const dbFilterDispTag = useMemo(() => {
    const dispTags: Record<string, string> = {};
    dbFilter.forEach((db: string) => {
      dispTags[db] = db;
    });
    return dispTags;
  }, [dbFilter]);

  const header = useMemo(() => {
    if (!datastores) return [];
    return [
      {
        id: 1,
        label: "Table Names",
        width: "15%",
        uid: "table-name",
        render: (item: Dataset) => (
          <div className="cursor-pointer">
            <Stack className={"al-text-brand "} align="start">
              {getIconByDatastoreType("snowflake")}
              <div className="al-break-all">{item.rk.name}</div>
            </Stack>
            <Tooltip title={item.rk.path}>
              <Stack className={"al-break-words"} align="start">
                <div className="al-pt-2">
                  <TablePath text={item.rk.path} maxLength={100} />
                </div>
                <CopyIconButton value={item.rk.path} colorClassName="al-text-gray-500" isLight />
              </Stack>
            </Tooltip>
          </div>
        ),
        filter: {
          filterType: "multiDropdown",
          multiSelectFilterOpenOnHover: false,
          multiSelectFilterOpenOnFirstHover: true,
          options: [
            {
              value: "table-name-filter",
              label: "Table Name",
              subComponent: (close: () => void) => {
                return (
                  <APIFilterDropdown
                    key="table-name-filter"
                    id="table-name-filter"
                    label={"Table Name"}
                    value={tableNameFilter || []}
                    apiFunction={getTableNameFilters}
                    onChange={(value: string[]) => {
                      updateSearchParamsByKey({ table: value });
                      close();
                    }}
                    seeMore={true}
                    searchKey="table"
                  />
                );
              },
            },
            {
              value: "schema-filter",
              label: "Schema",
              subComponent: (close: () => void) => {
                return (
                  <APIFilterDropdown
                    key="schema-filter"
                    id="schema-filter"
                    label={"Schema"}
                    value={schemaFilter || []}
                    apiFunction={getSchemaFilters}
                    onChange={(value: string[]) => {
                      updateSearchParamsByKey({ schema: value });
                      close();
                    }}
                    seeMore={true}
                    searchKey="schema"
                  />
                );
              },
            },
            {
              value: "db-filter",
              label: "Database",
              subComponent: (close: () => void) => {
                return (
                  <APIFilterDropdown
                    key="db-filter"
                    id="db-filter"
                    label={"Database"}
                    value={dbFilter || []}
                    apiFunction={getDatabaseFilters}
                    onChange={(value: string[]) => {
                      updateSearchParamsByKey({ database: value });
                      close();
                    }}
                    seeMore={true}
                    searchKey="database"
                  />
                );
              },
            },
          ],
        },
      },
      {
        id: 2,
        label: "Annualized Total Cost",
        uid: "total-cost",
        width: "8%",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>
            {formatNumber(item.total_cost, { currency })}
          </div>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
          sortParamValue: "total_cost",
        },
        filter: {
          searchKey: "totalcost",
          filterType: "text",
          value: totalCostFilter,
          placeHolder: `Specify cost in ${currencySymbol}`,
          label: "Annualized Total Cost",
          onChange: (value: string) => {
            updateSearchParamsByKey({ totalcost: value });
          },
        },
      },

      {
        id: 3,
        label: (
          <span>
            Annualized Insertion / Refresh Cost
            {showSnowflakeNonEnterpriseInfo && <span className="text-danger al-text-lg">*</span>}
          </span>
        ),
        uid: "insertion-cost",
        width: "9%",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>
            {formatNumber(item.insertion_cost, { currency })}
          </div>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
          sortParamValue: "insertion_cost",
        },
        filter: {
          searchKey: "insertioncost",
          filterType: "text",
          value: insertionCostFilter,
          placeHolder: `Specify cost in ${currencySymbol}`,
          label: "Annualized Insertion / Refresh Cost",
          onChange: (value: string) => {
            updateSearchParamsByKey({ insertioncost: value });
          },
        },
      },

      {
        id: 4,
        label: "Annualized Storage Cost",
        width: "8%",
        tooltipInfo:
          "(ACTIVE_BYTES + TIME_TRAVEL_BYTES + FAILSAFE_BYTES + RETAINED_FOR_CLONE_BYTES) / (1024 ^4) * 23 * 12",
        uid: "storage-cost",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>
            {formatNumber(item.storage_cost, { currency })}
          </div>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
          sortParamValue: "storage_cost",
        },
        filter: {
          searchKey: "storagecost",
          filterType: "text",
          value: storageCostFilter,
          placeHolder: `Specify cost in ${currencySymbol}`,
          label: "Annualized Storage Cost",
          onChange: (value: string) => {
            updateSearchParamsByKey({ storagecost: value });
          },
        },
      },

      {
        id: 5,
        label: "Annualized Clustering Cost",
        uid: "clustering-cost",
        width: "8%",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>
            {formatNumber(item.clustering_cost, { currency })}
          </div>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
          sortParamValue: "clustering_cost",
        },
        filter: {
          searchKey: "clusteringcost",
          filterType: "text",
          value: clusteringCostFilter,
          placeHolder: `Specify cost in ${currencySymbol}`,
          label: "Annualized Clustering Cost",
          onChange: (value: string) => {
            updateSearchParamsByKey({ clusteringcost: value });
          },
        },
      },
      {
        id: 6,
        label: "Insights",
        uid: "insights",
        width: "8.5%",
        render: (item: Dataset) => <ColorInsightTags tags={item.tag_rks} />,
        filter: {
          id: "insight-filter",
          searchKey: "insight",
          filterType: "dropdown",
          value: tagFilter,
          options: tagFilters || [],
          onChange: (value: string[]) => {
            updateSearchParamsByKey({ insight: value });
          },
        },
      },

      {
        id: 7,
        label: (
          <span>
            Reads
            {showSnowflakeNonEnterpriseInfo && <span className="text-danger al-text-lg">*</span>}
          </span>
        ),
        uid: "reads",
        width: "7.5%",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>
            {item.tag_rks && item.tag_rks.includes(DYNAMIC_TAG)
              ? "N/A"
              : formatNumber(item.reads ?? 0)}
          </div>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
        },
        filter: {
          searchKey: "reads",
          filterType: "text",
          value: readsFilter,
          placeHolder: "Specify reads",
          label: "Reads",
          onChange: (value: string) => {
            updateSearchParamsByKey({ reads: value });
          },
        },
      },

      {
        id: 8,
        label: (
          <span>
            Writes / Refreshes
            {showSnowflakeNonEnterpriseInfo && <span className="text-danger al-text-lg">*</span>}
          </span>
        ),
        width: "7.5%",
        uid: "writes",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>
            {formatNumber(item.writes)}
          </div>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
        },
        filter: {
          searchKey: "writes",
          filterType: "text",
          value: writesFilter,
          placeHolder: "Specify writes / refreshes",
          label: "Writes / Refreshes",
          onChange: (value: string) => {
            updateSearchParamsByKey({ writes: value });
          },
        },
      },

      {
        id: 9,
        label: "Read Write Ratio",
        width: "7.5%",
        uid: "read-write-ratio",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>
            {item.read_write_ratio}
          </div>
        ),
        sort: {
          sortSearchKey: "sortAttribute",
          sortParamValue: "read_write_ratio",
        },
        filter: {
          searchKey: "readWriteRatio",
          filterType: "text",
          value: readWriteRatioFilter,
          placeHolder: "Specify read write ratio",
          label: "Read Write Ratio",
          onChange: (value: string) => {
            updateSearchParamsByKey({ readWriteRatio: value });
          },
        },
      },
      {
        id: 10,
        label: "Owner",
        uid: "owner",
        width: "6%",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>{item.owner}</div>
        ),
        filter: {
          id: "owner-filter",
          searchKey: "owner",
          filterType: "apiDropdown",
          value: ownerFilter,
          apiFunction: getTableOwnerFilters,
          seeMore: true,
        },
      },
      {
        id: 11,
        label: "Instance",
        uid: "instance_id",
        width: "7.5%",
        render: (item: Dataset) => (
          <div className={classNames("al-break-words", "al-break-all")}>
            <div>
              {getInstanceName(item.instance_id, datastores)}

              {showSnowflakeNonEnterpriseInfo && item.instance_id && (
                <Badge className="!al-bg-bg-brand !al-text-gray !al-font-normal">
                  {getInstanceEdition(item.instance_id, datastores)}
                </Badge>
              )}
            </div>
          </div>
        ),
        filter: {
          id: "instance-filter",
          searchKey: "instance",
          filterType: "dropdown",
          value: instanceFilter,
          options: datastores.map((instance: Datastore) => ({
            label: instance.name,
            value: instance.instance_id,
          })),
          onChange: (value: string[]) => {
            updateSearchParamsByKey({ instance: value });
          },
        },
      },
    ] as TableHeaderType<Dataset>[];
  }, [
    tableNameFilter,
    schemaFilter,
    dbFilter,
    tagFilter,
    ownerFilter,
    instanceFilter,
    sortAttribute,
    sortOrder,
    currency,
    currencySymbol,
    tagFilters,
    datastores,
  ]);

  if (isTablesLoading) return <RelativeComponentLoader label={"Loading..."} />;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error instanceof Error
            ? error.message
            : String(error) || "Failed to load warehouse details"}
        </AlertDescription>
      </Alert>
    );
  }

  if (
    tables &&
    tables.total === 0 &&
    !(
      totalCostFilter ||
      insertionCostFilter ||
      storageCostFilter ||
      clusteringCostFilter ||
      (tableNameFilter && tableNameFilter.length > 0) ||
      (schemaFilter && schemaFilter.length > 0) ||
      (dbFilter && dbFilter.length > 0) ||
      (tagFilter && tagFilter.length > 0) ||
      readsFilter ||
      writesFilter ||
      readWriteRatioFilter ||
      (ownerFilter && ownerFilter.length > 0) ||
      (instanceFilter && instanceFilter.length > 0)
    )
  ) {
    return <NoDatasets />; // only returned if no tables were found without applying any of the filters.
  }

  if (!tables || !datastores) {
    return <RelativeComponentLoader label={"Loading..."} />;
  }

  return (
    <>
      <div className={"al-p-4 al-flex al-flex-col al-w-full al-bg-white al-rounded-md"}>
        <TableDataHeader
          isFiltersLoading={isTagFiltersLoading}
          numTables={tables.total}
          tableFilters={getRequestBody()}
          tableNameFilterDispTag={tableNameFilterDispTag}
          schemaFilterDispTag={schemaFilterDispTag}
          dbFilterDispTag={dbFilterDispTag}
          getFilterParams={getRequestBody}
          datastores={datastores}
        />
        <DatasetSummaryCard
          costs={annualizedCost}
          isLoading={isAnnualizedCostLoading}
          instanceIds={instanceFilter}
          databaseNames={dbFilter}
          schemaNames={schemaFilter}
          tableNames={tableNameFilter}
          totalCost={totalCostFilter}
          insertionCost={insertionCostFilter}
          storageCost={storageCostFilter}
          clusteringCost={clusteringCostFilter}
          tags={tagFilter}
          minReads={readsFilter}
          minWrites={writesFilter}
          minReadWriteRatio={readWriteRatioFilter}
          owner={ownerFilter}
        />
        {showSnowflakeNonEnterpriseInfo && (
          <Stack justify="end">
            <Alert
              variant="info"
              className="!al-w-auto al-bg-bg-brand"
              aria-label="Snowflake Enterprise feature notice"
            >
              <AlertDescription>
                <Stack direction="row" align="center" gap="sm">
                  <IconInfoCircle className="al-h-4 al-w-4" />
                  Columns marked as <span className="text-danger al-text-lg">*</span> are only
                  available for Snowflake Enterprise Accounts (or higher).
                </Stack>
              </AlertDescription>
            </Alert>
          </Stack>
        )}
        <DataTable
          header={header}
          data={tables}
          idKey={(item: Dataset) => item.rk.rk}
          getRowUrl={(item: Dataset) => `/datasets/${item.resource_id}`}
          getRowState={item => ({ entityName: item.rk.name })}
          resetPage={resetPage}
          applicableFor={FilterApplicableFor.DATASET_LIST}
          columnPickerPortal="columnpicker-container"
          minWidth={2000}
        />
        <Paginate
          itemCount={tables.total}
          page={page}
          pageSize={size}
          numPages={tables.pages || 0}
          onPageClick={setPage}
          onPageSizeChange={updatePageSize}
          showPageSize
        />
      </div>
    </>
  );
};

export default DatasetsList;
